import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { logoUrl, type ClientLogo } from '../data/clientLogos';

/** Fetched SVG path data per slug (null = not available) — shared, so every logo loads once */
const cache = new Map<string, Promise<string | null>>();
export function loadLogo(slug: string): Promise<string | null> {
  let p = cache.get(slug);
  if (!p) {
    p = fetch(logoUrl(slug)).then(r => r.ok ? r.text() : null).then(svg => {
      if (!svg) return null;
      const paths = Array.from(svg.matchAll(/<path[^>]*\sd="([^"]+)"/g)).map(m => m[1]);
      return paths.length ? paths.join(' ') : null;
    }).catch(() => null);
    cache.set(slug, p);
  }
  return p;
}
interface LogoMarkProps {
  logo: ClientLogo;
  className?: string;
}

/**
 * A white client logo, cropped to its real shape (the icon library pads everything into a square,
 * which made wordmarks tiny), so marks and wordmarks can share one height. Falls back to the
 * brand name if the logo isn't available.
 */
export function LogoMark({
  logo,
  className = ''
}: LogoMarkProps) {
  const [d, setD] = useState<string | null | undefined>(undefined);
  const [box, setBox] = useState<string | null>(null);
  /** Height as a fraction of the slot, so every logo carries about the same visual weight */
  const [heightFrac, setHeightFrac] = useState(1);
  const pathRef = useRef<SVGPathElement>(null);
  useEffect(() => {
    let alive = true;
    setBox(null);
    loadLogo(logo.slug).then(path => alive && setD(path));
    return () => {
      alive = false;
    };
  }, [logo.slug]);

  // Crop the viewBox to the path's actual bounds
  useLayoutEffect(() => {
    const el = pathRef.current;
    if (!el || !d) return;
    const b = el.getBBox();
    if (b.width > 0 && b.height > 0) {
      setBox(`${b.x} ${b.y} ${b.width} ${b.height}`);
      setHeightFrac(weightHeight(b.width / b.height));
    }
  }, [d]);
  if (d === null) {
    return <span className={`flex items-center whitespace-nowrap text-[clamp(1rem,2.2vw,2.4rem)] font-extrabold tracking-[-0.04em] text-white ${className}`} data-id="element-121" data-mp="30llqtk7cvcv4">
        {logo.name}
      </span>;
  }
  return <svg role="img" aria-label={logo.name} viewBox={box ?? '0 0 24 24'} preserveAspectRatio="xMidYMid meet" className={`block w-auto max-w-full ${className}`} style={{
    visibility: box ? 'visible' : 'hidden',
    height: `${heightFrac * 100}%`
  }} data-id="element-122" data-mp="3aji68o7bstz1">
      {d && <path ref={pathRef} d={d} fill="#ffffff" data-id="element-123" data-mp="3fiusp7uabz13" />}
    </svg>;
}

/**
 * Optical size normalisation: compact symbols fill the slot height, wide wordmarks get shorter so
 * their area (visual weight) roughly matches. Height ∝ aspect^-0.5 keeps area constant; the softer
 * 0.42 exponent lets wordmarks stay a touch wider, which reads as equal to the eye.
 */
function weightHeight(aspect: number): number {
  const REF = 1.15; // aspect that fills the full slot height
  return Math.min(1, Math.max(0.32, Math.pow(REF / Math.max(aspect, 0.01), 0.42)));
}